import React from 'react';
import isPlainObject from 'lodash/isPlainObject';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';

import { requestStatuses, responseStatuses, sensorFeatureKeys } from '../constants';
import { InfoButtonWithOnlineHelp } from 'sharedComponents';

// eslint-disable-next-line no-unused-vars
const timeConvert = (minutes) => {

    if (minutes === null) {
        return '-';
    }

    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    let result = '';
    if (h !== 0) {
        const days = h / 24;
        if (days >= 1) {
            const hours = h - 24 * Math.floor(days);
            result = Math.floor(days) + 'd ' + hours + 'h ';
        }
        else {
            result = h + 'h ';
        }
    }
    if (m !== 0) {
        result += m + 'm';
    }

    return result;
};

const getWholeMinutes = (decimalMinutes, translate) => {
    if (decimalMinutes === null) {
        return '-';
    }
    return Math.floor(decimalMinutes) + ' ' + translate('ABB.Powertrain.Frontend.sensorMinutesLabel');
};

const operatingModeTypes = {
    0: 'ABB.Powertrain.Frontend.StandardMode',
    1: 'ABB.Powertrain.Frontend.FlightMode',
    2: 'ABB.Powertrain.Frontend.ArmedMode',
    3: 'ABB.Powertrain.Frontend.DeepSleepMode'
};

const splitString = (string, size) => {
    if (typeof string === 'string') {
        const re = new RegExp('.{1,' + size + '}', 'g');
        return string.match(re);
    }
};

// Helper function to format values with units
const formatValueWithUnit = (value, unit, fallback = '-') => {
    return value ? `${value} ${unit}` : fallback;
};

// Helper function to format timezone
const formatTimeZone = (featureValue) => {
    const hours = featureValue / 3600;
    return 'UTC' + (hours < 0 ? '-' : '+') + hours.toString();
};

// Helper function to format enabled/disabled features
const formatEnabledDisabled = (featureValue, translate) => {
    if (featureValue === null) { return '-'; }
    return featureValue === 1
        ? translate('ABB.Powertrain.Frontend.sensorFeatureEnabledLabel')
        : translate('ABB.Powertrain.Frontend.sensorFeatureDisabledLabel');
};

// Helper function to format accelerometer range
const formatAccelerometerRange = (featureValue, translate) => {
    if (featureValue === 0) {
        return translate('ABB.Powertrain.Frontend.sensorFeatureAutoLabel');
    }
    return formatValueWithUnit(featureValue, 'g');
};

// Helper function to format RSSI value
const formatRSSIValue = (featureValue) => {
    return featureValue?.rssiValue ? `${featureValue.rssiValue} dBm` : '-';
};

// Helper function to format join key
const formatJoinKey = (featureValue) => {
    return splitString(featureValue, 8).join('-');
};


export const formatSensorFeatureValue = (feature, translate) => {
    const { featureKeyName, featureValue } = feature || {};

    switch (featureKeyName) {
        case sensorFeatureKeys.PowerSavingTimeZone:
            return formatTimeZone(featureValue);

        case sensorFeatureKeys.BatteryLevel:
            return formatValueWithUnit(featureValue, 'V');

        case sensorFeatureKeys.MeasuredCurrent:
            return formatValueWithUnit(featureValue, 'A');

        case sensorFeatureKeys.RunningStateCalibration:
            return formatValueWithUnit(featureValue, 'g RMS');

        case sensorFeatureKeys.ThreeAxisVibrationRawData:
        case sensorFeatureKeys.EnableSpeedSync:
            return formatEnabledDisabled(featureValue, translate);

        case sensorFeatureKeys.SensorMeasurementInterval:
            return getWholeMinutes(featureValue, translate);

        case sensorFeatureKeys.AccelerometerRangeConfiguration:
            return formatAccelerometerRange(featureValue, translate);

        case sensorFeatureKeys.JoinKey:
            return formatJoinKey(featureValue);

        case sensorFeatureKeys.SensorRSSIValue:
            return formatRSSIValue(featureValue);

        default:
            return !isPlainObject(featureValue) && featureValue || '-';
    }
};

export const getOperatingModeTypeLabel = (operatingModeTypeID) => {
    return operatingModeTypes[Number(operatingModeTypeID)] + 'Label';
};


export const getRequestStatusText = (featureValue, translate) => {
    switch (featureValue) {
        case requestStatuses.NOT_REQUESTED_INITIAL: return translate('ABB.Powertrain.Frontend.notRequestedLabel');
        case requestStatuses.NOT_REQUESTED: return translate('ABB.Powertrain.Frontend.notRequestedLabel');
        case requestStatuses.PENDING: return translate('ABB.Powertrain.Frontend.pendingLabel');
        case requestStatuses.COMPLETED: return translate('ABB.Powertrain.Frontend.completedLabel');
        case requestStatuses.ERROR: return translate('ABB.Powertrain.Frontend.errorLabel');
        default: return null;
    }
};

export const getResponseStatusText = (featureValue, translate) => {
    switch (featureValue) {
        case responseStatuses.COMPLETED_SUCCESSFULLY: return translate('ABB.Powertrain.Frontend.lbl_sf_completed_successfully');
        case responseStatuses.COULD_NOT_CONNECT_TO_THE_SENSOR: return translate('ABB.Powertrain.Frontend.lbl_sf_could_not_connect');
        case responseStatuses.INTERNAL_ERROR: return translate('ABB.Powertrain.Frontend.lbl_sf_internal_error');
        case responseStatuses.NO_MEASUREMENT_AVAILABLE: return translate('ABB.Powertrain.Frontend.lbl_sf_no_measurements');
        case responseStatuses.SENSOR_NOT_FOUND: return translate('ABB.Powertrain.Frontend.lbl_sf_sensor_not_found');
        case responseStatuses.WRONG_PASSKEY: return translate('ABB.Powertrain.Frontend.lbl_sf_wrong_passkey');
        default: return null;
    }
};

export const getVisibleFeatures = (sensorFeatures) => {
    const sortedSensorFeatures = sortBy(sensorFeatures, el => el.sequenceNo);
    return filter(sortedSensorFeatures, el => el.isVisible);
};

export const getColumns = (translate) => {
    return [
        {
            title: translate('ABB.Powertrain.Frontend.MsrDetailsType'),
            dataIndex: 'SensorName',
            key: 'SensorName',
            isSorting: true,
            width: 150,
        },
        {
            title: translate('ABB.Powertrain.Frontend.MsrDetailsPosition'),
            dataIndex: 'SensorPosition',
            key: 'SensorPosition',
            width: 250,
            isSorting: true
        },
        {
            title: translate('ABB.Powertrain.Frontend.MsrDetailsChannel'),
            dataIndex: 'ChannelId',
            key: 'ChannelId',
            width: 150,
            isSorting: true
        },
    ];
};
export const getColumnsForRssiValue = (translate) => {
    return [
        {
            title: translate('ABB.Powertrain.Frontend.GatewayMacAddress'),
            dataIndex: 'macAddress',
            key: 'macAddress',
            isSorting: true,
            width: 250,
        },
        {
            title:
                <>
                    {translate('ABB.Powertrain.Frontend.SensorToGatewayRSSIValue')}
                    <InfoButtonWithOnlineHelp height={16} width={16} placement='top' infoText={translate('ABB.Powertrain.Frontend.SensorToGatewayRSSIValueInfo')} withoutLink={true} />
                </>
            ,
            dataIndex: 'rssiAvgValue',
            key: 'rssiAvgValue',
            width: 250,
            isSorting: true,
            render: (value) => value !== null && value !== undefined ? `${Math.round(value)} dBm` : '-'
        },
    ];
};
